import React, { useState, useMemo } from 'react';
import { Table, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './TestKitchen.css';
import { FaEdit, FaTrash, FaPlus, FaEye, FaFilePdf, FaToggleOn, FaToggleOff, FaExternalLinkAlt } from 'react-icons/fa';
import AddTestKitchenItemModal from './AddTestKitchenItemModal';
import { 
  useGetTestKitchenItemsQuery, 
  useAddTestKitchenItemMutation, 
  useUpdateTestKitchenItemMutation, 
  useDeleteTestKitchenItemMutation,
  useToggleTestKitchenItemStatusMutation
} from "../Services/SecondaryApi";
import Swal from 'sweetalert2';
import SearchImg from "../Assets/Images/search-img.svg";

const TestKitchen = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    dietary: [],
    operational: [],
    service: [],
    seasonal: []
  });

  const { 
    data: items = [], 
    isLoading, 
    error,
    refetch 
  } = useGetTestKitchenItemsQuery();

  const [deleteItem] = useDeleteTestKitchenItemMutation();
  const [toggleStatus] = useToggleTestKitchenItemStatusMutation();
  const [addItem] = useAddTestKitchenItemMutation();
  const [updateItem] = useUpdateTestKitchenItemMutation();

  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Process and sort items
  const sortedItems = useMemo(() => {
    console.log('Raw items data:', items);
    
    // Check if items exists and has a data property
    if (!items?.data) {
      console.log('No items.data found:', items);
      return [];
    }
    
    // Make sure items.data is an array before processing
    const itemsData = Array.isArray(items.data) ? items.data : [];
    
    // Process items to ensure consistent data structure
    const processedItems = itemsData.map(item => {
      // Log the item structure for debugging
      const hasPresignedUrl = 'presigned_item_image_url' in item && Boolean(item.presigned_item_image_url);
      const hasItemImage = 'item_image' in item && Boolean(item.item_image);
      
      console.log('Processing item:', {
        id: item.id,
        name: item.name,
        hasPresignedUrl,
        presignedUrl: item.presigned_item_image_url,
        hasItemImage,
        itemImage: item.item_image,
        allKeys: Object.keys(item)
      });
      
      // Construct the final image URL
      let finalImageUrl = null;
      if (hasPresignedUrl) {
        finalImageUrl = item.presigned_item_image_url;
      } else if (hasItemImage) {
        // If we only have item_image, construct the URL
        finalImageUrl = `https://dev-ccc-data.s3.ap-southeast-2.amazonaws.com/${item.item_image}`;
      }
      
      console.log('Final image URL for item', item.id, ':', finalImageUrl);
      
      // Return the item with the processed image URL
      return {
        ...item,
        imageUrl: finalImageUrl
      };
    });
    
    // Sort the items if needed
    const sortableItems = [...processedItems];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [items, sortConfig]);

  // Filter items based on search term and active filters
  const filteredItems = useMemo(() => {
    if (!sortedItems) return [];
    
    return sortedItems.filter(item => {
      // Search term filter
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.disclaimer && item.disclaimer.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Tag filters
      const matchesDietary = activeFilters.dietary.length === 0 || 
                           item.dietary_tags.some(tag => activeFilters.dietary.includes(tag.id));
      
      const matchesOperational = activeFilters.operational.length === 0 || 
                               item.operational_tags.some(tag => activeFilters.operational.includes(tag.id));
      
      const matchesService = activeFilters.service.length === 0 || 
                           item.service_tags.some(tag => activeFilters.service.includes(tag.id));
      
      const matchesSeasonal = activeFilters.seasonal.length === 0 || 
                            item.seasonal_tags.some(tag => activeFilters.seasonal.includes(tag.id));
      
      return matchesSearch && matchesDietary && matchesOperational && matchesService && matchesSeasonal;
    });
  }, [sortedItems, searchTerm, activeFilters]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteItem = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteItem(id);
        refetch();
        Swal.fire('Deleted!', 'The item has been deleted.', 'success');
      } catch (error) {
        Swal.fire('Error!', 'Failed to delete the item.', 'error');
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await toggleStatus({ id, status: !currentStatus });
      refetch();
      Swal.fire('Success!', 'Status updated successfully.', 'success');
    } catch (error) {
      Swal.fire('Error!', 'Failed to update status.', 'error');
    }
  };

  const handleFilterChange = (tagType, tagId) => {
    setActiveFilters(prev => ({
      ...prev,
      [tagType]: prev[tagType].includes(tagId)
        ? prev[tagType].filter(id => id !== tagId)
        : [...prev[tagType], tagId]
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Helper function to get badge color based on tag type
  const getBadgeColor = (type) => {
    switch(type) {
      case 'dietary': return '#28a745';
      case 'operational': return '#17a2b8';
      case 'service': return '#6f42c1';
      case 'seasonal': return '#fd7e14';
      default: return '#6c757d';
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (editingItem) {
        // For updates, wrap the data in a FormData object if it contains a file
        let payload = itemData;
        if (itemData.pdf_file) {
          const formData = new FormData();
          Object.keys(itemData).forEach(key => {
            if (key === 'dietary_tag_ids' || key === 'operational_tag_ids' || 
                key === 'service_tag_ids' || key === 'seasonal_tag_ids') {
              // Handle array fields
              itemData[key].forEach(value => formData.append(`${key}[]`, value));
            } else if (key === 'status') {
              // Convert boolean to string '1' or '0' for FormData
              formData.append(key, itemData[key] ? '1' : '0');
            } else if (key !== 'pdf_file') {
              formData.append(key, itemData[key]);
            }
          });
          // Add the file if it exists
          if (itemData.pdf_file) {
            formData.append('pdf_file', itemData.pdf_file);
          }
          payload = { id: editingItem.id, data: formData };
        } else {
          // For non-file updates, just pass the data as is
          payload = { id: editingItem.id, data: itemData };
        }
        await updateItem(payload).unwrap();
      } else {
        // For new items, just pass the data as is (the modal handles FormData for creates)
        await addItem(itemData).unwrap();
      }
      refetch();
      setShowModal(false);
      setEditingItem(null);
      Swal.fire('Success!', `Item ${editingItem ? 'updated' : 'added'} successfully.`, 'success');
    } catch (error) {
      console.error('Error saving item:', error);
      Swal.fire('Error!', `Failed to ${editingItem ? 'update' : 'add'} item: ${error?.data?.message || error.message}`, 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Failed to load Test Kitchen items. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="container-fluid py-4">
      <h3>Test Kitchen</h3>
      
      <div className="table-wrapper-div">
        <div className="sub-haed-table">
          <div className="d-flex justify-content-space-between">
            <div className="search-bar">
              <img src={SearchImg} alt="Search" className="SearchIcon"/>
              <input
                type="text"
                placeholder="Search test kitchen items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="add-div">
              <button className="primary-btn" onClick={handleAddItem}>
                <FaPlus className="me-1" /> Add Item
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <Table className="table-common">
            <thead>
              <tr>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('id')}
                >
                  #
                  {sortConfig.key === 'id' && (
                    <span className="ms-1">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                >
                  Name
                  {sortConfig.key === 'name' && (
                    <span className="ms-1">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="text-center">Image</th>
                <th className="text-center">Disclaimer</th>
                <th className="text-center">Tags</th>
                <th className="text-center">PDF</th>
                <th className="text-center">Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.name}</td>
                    <td className="text-center">
                      {item.imageUrl ? (
                        <div className="d-flex justify-content-center">
                          <div style={{ 
                            width: '50px', 
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5'
                          }}>
                            <img 
                              src={item.imageUrl} 
                              alt={item.name || 'Item image'}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                margin: 'auto',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                willChange: 'opacity'
                              }}
                              onLoad={(e) => {
                                console.log('Image loaded successfully:', item.imageUrl);
                                e.target.style.opacity = 1;
                              }}
                              onError={(e) => {
                                // Only set the fallback if the image hasn't already loaded
                                if (e.target.style.opacity !== '1') {
                                  console.log('Error loading image, using fallback:', {
                                    url: item.imageUrl,
                                    timestamp: new Date().toISOString()
                                  });
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMSI+PHBhdGggZD0iTTMuMXYxOGgxOFY1LjFIMy4xWiIvPjxwYXRoIGQ9Ik0xNSAxOWg0TTUgMTloNHYtNEw5IDlsNCA0LjVWMTloNHYtNS41Ii8+PC9zdmc+';
                                  e.target.style.opacity = 1;
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center">
                          <div style={{
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #eee',
                            borderRadius: '4px',
                            color: '#999',
                            fontSize: '10px',
                            textAlign: 'center',
                            padding: '5px'
                          }}>
                            No Image
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      {item.disclaimer ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip 
                              id={`tooltip-${item.id}`}
                              className="custom-tooltip"
                              style={{
                                '--tooltip-bg': 'white',
                                '--tooltip-color': 'black',
                                '--tooltip-max-width': '400px',
                                '--tooltip-padding': '10px',
                                '--tooltip-border-radius': '4px',
                                '--tooltip-box-shadow': '0 2px 10px rgba(0,0,0,0.15)'
                              }}
                            >
                              <div style={{ textAlign: 'left' }}>{item.disclaimer}</div>
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'pointer' }}>
                            <FaEye className="text-info" />
                          </span>
                        </OverlayTrigger>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        {[
                          ...(item.dietary_tags || []).map(tag => ({ ...tag, type: 'dietary' })),
                          ...(item.operational_tags || []).map(tag => ({ ...tag, type: 'operational' })),
                          ...(item.service_tags || []).map(tag => ({ ...tag, type: 'service' })),
                          ...(item.seasonal_tags || []).map(tag => ({ ...tag, type: 'seasonal' }))
                        ].slice(0, 3).map((tag, index) => (
                          <span 
                            key={`${tag.type}-${tag.id}`}
                            className="badge"
                            style={{
                              backgroundColor: getBadgeColor(tag.type),
                              color: 'white',
                              fontSize: '0.8rem',
                              fontWeight: 'normal',
                              borderRadius: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '120px',
                              display: 'inline-block'
                            }}
                            title={`${tag.type}: ${tag.name}`}
                          >
                            {tag.name}
                          </span>
                        ))}
                        {[
                          ...(item.dietary_tags || []),
                          ...(item.operational_tags || []),
                          ...(item.service_tags || []),
                          ...(item.seasonal_tags || [])
                        ].length > 3 && (
                          <span 
                            className="badge bg-secondary"
                            style={{
                              fontSize: '0.8rem',
                              fontWeight: 'normal',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            title="View all tags"
                            onClick={() => handleEditItem(item)}
                          >
                            +{[
                              ...(item.dietary_tags || []),
                              ...(item.operational_tags || []),
                              ...(item.service_tags || []),
                              ...(item.seasonal_tags || [])
                            ].length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      {item.presigned_pdf_url ? (
                        <a 
                          href={item.presigned_pdf_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-icon text-danger"
                          title="View PDF"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '24px',
                            height: '24px',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease',
                            fontSize: '16px'
                          }}
                        >
                          <FaFilePdf />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      <button 
                        className={`btn btn-sm ${item.status ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => handleStatusToggle(item.id, item.status)}
                        style={{
                          minWidth: '80px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        {item.status ? (
                          <>
                            <FaToggleOn /> Active
                          </>
                        ) : (
                          <>
                            <FaToggleOff /> Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center">
                        <button 
                          className="btn-outline me-2"
                          onClick={() => handleEditItem(item)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-outline"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    No test kitchen items found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      
      {totalPages > 1 && (
        <div className="pagination-container mt-3">
          <nav aria-label="Page navigation">
            <ul className="pagination justify-content-center mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <li key={pageNum} className="page-item">
                    <button
                      className={`page-link ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => paginate(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>
          <div className="text-center text-muted mt-2">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
      
      <AddTestKitchenItemModal
        show={showModal}
        onHide={() => setShowModal(false)}
        item={editingItem}
        onSave={handleSaveItem}
      />
    </div>
  );
};

export default TestKitchen;
